import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { addBrandOpsKnowledge } from '../../../redux/brandOps/actions';
import { uploadUserMediaLibraryImage } from '../../../redux/userMedia/actions';
import type { IUploadUserMediaLibraryImageResult } from '../../../redux/userMedia/actions';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { extractTextFromPdf } from '../../../utils/resumeParser';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';

const titleFromFilename = (name: string) => name.replace(/\.[^.]+$/, '').trim() || name;

const isTextFile = (file: File) => {
    if (file.type.startsWith('text/')) return true;
    return /\.(txt|md|markdown|csv)$/i.test(file.name);
};

const isPdfFile = (file: File) =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const readAsText = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsText(file);
    });

/** Drop images, PDFs, or text docs → register as brand knowledge. */
export default function KnowledgeDropZone({ brandId, disabled }: { brandId?: string; disabled?: boolean }) {
    const dispatchP = useDispatchPromise();
    const [dragging, setDragging] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleFiles = async (files: FileList | null) => {
        if (!brandId || !files?.length) return;
        const list = Array.from(files);
        const images = list.filter(f => f.type.startsWith('image/'));
        const texts = list.filter(f => isTextFile(f));
        const pdfs = list.filter(f => isPdfFile(f));
        const skipped = list.length - images.length - texts.length - pdfs.length;
        if (skipped > 0) {
            showToastMessage('Skipped unsupported files — use images, PDFs, or .txt/.md', 'error');
        }
        if (!images.length && !texts.length && !pdfs.length) return;

        setBusy(true);
        let added = 0;

        for (const file of texts) {
            try {
                const text = (await readAsText(file)).trim();
                if (!text) throw new Error('Empty text file');
                await dispatchP(
                    addBrandOpsKnowledge({
                        brandId,
                        body: {
                            title: titleFromFilename(file.name),
                            text_excerpt: text.slice(0, 20000),
                            mime_type: file.type || 'text/plain',
                        },
                    }),
                );
                added += 1;
            } catch (err: any) {
                showToastMessage(`${file.name}: ${err?.message || 'upload failed'}`, 'error');
            }
        }

        for (const file of pdfs) {
            try {
                const text = (await extractTextFromPdf(file)).trim();
                if (!text) throw new Error('Could not extract text — PDF may be image-only');
                await dispatchP(
                    addBrandOpsKnowledge({
                        brandId,
                        body: {
                            title: titleFromFilename(file.name),
                            text_excerpt: text.slice(0, 20000),
                            mime_type: 'application/pdf',
                        },
                    }),
                );
                added += 1;
            } catch (err: any) {
                showToastMessage(`${file.name}: ${err?.message || 'PDF extraction failed'}`, 'error');
            }
        }

        for (const file of images) {
            try {
                const uploaded = (await dispatchP(
                    uploadUserMediaLibraryImage({ file, category: 'other', name: file.name }),
                )) as IUploadUserMediaLibraryImageResult;
                const asset = uploaded?.asset;
                if (!asset?.file_key) throw new Error('Upload did not return a file key');
                await dispatchP(
                    addBrandOpsKnowledge({
                        brandId,
                        body: {
                            title: titleFromFilename(file.name),
                            file_key: asset.file_key,
                            public_url: asset.url || uploaded.url,
                            mime_type: asset.mime_type || file.type,
                        },
                    }),
                );
                added += 1;
            } catch (err: any) {
                showToastMessage(`${file.name}: ${err?.message || 'upload failed'}`, 'error');
            }
        }

        setBusy(false);
        if (added) showToastMessage(`${added} file${added > 1 ? 's' : ''} added to vault`, 'success');
    };

    const off = disabled || busy;
    return (
        <Box
            onDragOver={e => {
                e.preventDefault();
                if (!off) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => {
                e.preventDefault();
                setDragging(false);
                if (!off) handleFiles(e.dataTransfer.files);
            }}
            sx={{
                border: `1px dashed ${dragging ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.15)'}`,
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                textAlign: 'center',
            }}>
            <CloudUploadOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 24 }} />
            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                {busy ? 'Processing…' : 'Drag & drop images, PDFs, or .txt/.md, or '}
                {!busy ? (
                    <Box component="label" sx={{ color: BRAND_LUXE_PRIMARY, cursor: 'pointer' }}>
                        browse
                        <input
                            hidden
                            type="file"
                            accept="image/*,.pdf,application/pdf,.txt,.md,.markdown,text/plain,text/markdown"
                            multiple
                            disabled={off}
                            onChange={e => {
                                handleFiles(e.target.files);
                                e.target.value = '';
                            }}
                        />
                    </Box>
                ) : null}
            </Typography>
            {!busy && (
                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.5 }}>
                    Images are OCR'd via AI · PDFs extracted client-side · Text added directly
                </Typography>
            )}
        </Box>
    );
}
